import { isEqual } from '@Sage/ssm-utils';
import moment from 'moment';

import Context from 'managers/Context';
import { isBackendFalsy } from '@Sage/ssm-utils/backendScalars';
import { getLiteral } from 'utils/getLiteral';
import { ensureRoute } from 'utils/routes';
import { logEvent } from 'utils/tracking';

export const toDate = (date) => {
    // Because 'DD/MM/YYYY h:mm:ss' ...
    if (!date) return date;
    const format = 'DD/MM/YYYY HH:mm:ss';
    return moment(date, format).toDate();
};

export const toDecimal = (value, decimals = 2) => {
    let v = value ? value.replace(',', '.') : value;
    if (v && !isNaN(v))
        v = Number(Math.round(Number(v) + 'e' + decimals) + 'e-' + decimals).toFixed(decimals);
    return v;
};

export const toNumber = (value) => (value ? value.replace(',', '') : value);

export const toBool = (value) => !isBackendFalsy(value);

export const normalizeSelectId = (value) =>
    value === undefined || value === null || value === '' || value === -1 || value === '-1'
        ? undefined
        : value;

export const toFloat = (value) => {
    let v = value ? value.replace(',', '.') : value || 0;
    if (v && !isNaN(v)) v = parseFloat(v);
    return v;
};

export const fromExtraFieldsEndpoint = (fields = [], mappedSchema) => {
    let flattenFields = {};
    fields.forEach((section) =>
        section.data.forEach((field) => {
            const fieldId = field.id;
            const fieldConfig = mappedSchema[fieldId];
            if (!fieldConfig) return;
            switch (fieldConfig.type) {
                case 'text':
                case 'textarea':
                    flattenFields[fieldId] = field.value;
                    break;
                case 'number':
                case 'integer':
                    flattenFields[fieldId] = toNumber(field.value);
                    break;
                case 'decimal':
                case 'currency':
                case 'percent':
                    flattenFields[fieldId] = toDecimal(field.value);
                    break;
                case 'dateTime':
                case 'datetime':
                case 'date':
                    flattenFields[fieldId] = toDate(field.value);
                    break;
                case 'bool':
                    flattenFields[fieldId] = toBool(field.value);
                    break;
                case 'singleValueList':
                    if (field.value && field.value !== '-1') {
                        flattenFields[fieldId] = {
                            value: field.value,
                            label: field.label,
                        };
                    }
                    break;
                case 'multipleValueList':
                    if (field.value && field.value !== '-1') {
                        let ids = field.value.split(';');
                        let values = field.label.split(';');
                        flattenFields[fieldId] = [];
                        for (let i = 0; i < ids.length; i++) {
                            flattenFields[fieldId].push({ value: ids[i], label: values[i] });
                        }
                    }
                    break;
                case 'fuzzySearchSingle':
                    if (field.value && field.value !== '-1') {
                        flattenFields[fieldId] = {
                            value: field.value,
                            label: field.label,
                        };
                    }
                    break;
                case 'fuzzySearchMultiple':
                    if (field.label && field.value && field.value !== '-1') {
                        let ids = field.value.split(';');
                        let values = field.label.split(';');
                        flattenFields[fieldId] = [];
                        if (ids.length === values.length) {
                            // value label
                            for (let i = 0; i < ids.length; i++) {
                                flattenFields[fieldId].push({ value: ids[i], label: values[i] });
                            }
                        }
                    }
                    break;
                default:
                    console.warn('[CRUD] Extra field type not contempled', field.type);
                    break;
            }
        }),
    );
    return flattenFields;
};

export const fromExtraFields = (fields = [], mappedSchema) =>
    fields.reduce((obj, field) => {
        const fieldId = field.field.replace('EXTRA_', '');
        const fieldConfig = mappedSchema[fieldId];
        if (!fieldConfig) return obj;
        switch (fieldConfig.type) {
            case 'text':
            case 'textarea':
                obj[fieldId] = field.value;
                break;
            case 'number':
            case 'integer':
                obj[fieldId] = toNumber(field.value);
                break;
            case 'decimal':
            case 'currency':
            case 'percent':
                obj[fieldId] = toDecimal(field.value);
                break;
            case 'dateTime':
            case 'datetime':
            case 'date':
                obj[fieldId] = toDate(field.value);
                break;
            case 'bool':
                obj[fieldId] = toBool(field.value);
                break;
            case 'singleValueList':
                if (field.id && field.id !== '-1') {
                    obj[fieldId] = { label: field.value, value: field.id };
                }
                break;
            case 'multipleValueList':
                if (field.id && field.id !== '-1') {
                    let ids = field.id.split(';');
                    let values = field.value.split(';');
                    obj[fieldId] = [];
                    for (let i = 0; i < ids.length; i++) {
                        obj[fieldId].push({ value: ids[i], label: values[i] });
                    }
                }
                break;
            case 'fuzzySearchSingle':
                if (field.id && field.id !== '-1') {
                    obj[fieldId] = {
                        value: field.id,
                        label: field.value,
                    };
                }
                break;
            case 'fuzzySearchMultiple':
                if (field.id && field.value && field.id !== '-1') {
                    let ids = field.id.split(';');
                    let values = field.value.split(';');
                    obj[fieldId] = [];
                    if (ids.length === values.length) {
                        // value label
                        for (let i = 0; i < ids.length; i++) {
                            obj[fieldId].push({ value: ids[i], label: values[i] });
                        }
                    }
                }
                break;
            default:
                console.warn('[CRUD] Extra field type not contempled', field.type);
                break;
        }

        return obj;
    }, {});

export const fromExtraFieldsNewEntity = (fields = [], mappedSchema) =>
    fields.reduce((obj, field) => {
        const fieldId = field.id;
        const fieldConfig = mappedSchema[fieldId];
        if (!fieldConfig) return obj;
        const value = field.value;
        switch (fieldConfig.type) {
            case 'text':
            case 'textarea':
                obj[fieldId] = value;
                break;
            case 'number':
            case 'integer':
                obj[fieldId] = !isNaN(value) && typeof value === 'string' ? toNumber(value) : value;
                break;
            case 'decimal':
            case 'currency':
            case 'percent':
                obj[fieldId] =
                    !isNaN(value) && typeof value === 'string' ? toDecimal(value) : value;
                break;
            case 'datetime':
            case 'date':
                if (!value) return obj;
                obj[fieldId] = toDate(value);
                break;
            case 'bool':
                obj[fieldId] = toBool(value);
                break;
            case 'singleValueList':
                if (!value) return obj;
                obj[fieldId] = { label: field.label, value: value };
                break;
            case 'multipleValueList':
                if (!value) return obj;
                const ids = value?.split(';').filter((el) => el !== '');
                const labels = field.label?.split(';').filter((el) => el !== '') || [];
                obj[fieldId] = ids.map((id, i) => ({ value: id, label: labels[i] || id }));
                break;
            case 'fuzzySearchSingle':
                if (!value) return obj;
                obj[fieldId] = {
                    value: value,
                    label: field.label,
                };
                break;
            case 'fuzzySearchMultiple':
                if (!value) return obj;
                let fuzzyIds = field.value.split(';');
                let fuzzyValues = field.label.split(';');
                obj[fieldId] = [];
                if (fuzzyIds.length === fuzzyValues.length) {
                    fuzzyIds.forEach((el, i) => {
                        if (el !== '') {
                            obj[fieldId].push({ value: fuzzyIds[i], label: fuzzyValues[i] });
                        }
                    });
                }
                break;
            default:
                console.warn('[CRUD] Extra field type not contemplated', field.type);
                break;
        }
        return obj;
    }, {});

function processFields(field, data, isBulk, dynamicHiddenMap) {
    let finalData = { ...data };

    let value = finalData[field.id];
    const finalId = field.serverId || field.id;

    if (
        field.readOnly ||
        field.hidden ||
        field.dynamicHidden ||
        dynamicHiddenMap[field.fieldConfiguration]
    ) {
        delete finalData[field.id];
        if (field.serverId && typeof field.serverId === 'object') {
            Object.values(field.serverId).forEach((serverFieldId) => {
                delete finalData[serverFieldId];
            });
        } else if (field.serverId && field.serverId !== field.id) {
            delete finalData[field.serverId];
        }
        return finalData;
    }

    switch (field.type) {
        case 'multipleValueList':
            value = (value || []).map((i) => i.value);
            finalData[finalId] = value;
            break;
        case 'singleValueList':
            finalData[finalId] = value?.value;
            break;
        case 'text':
        case 'textarea':
            finalData[finalId] = (value || '').trim();
            break;
        case 'bool':
            finalData[finalId] = value || false;
            break;
        default:
            finalData[finalId] = value;
            break;
    }

    // Removing non-visible fields data
    return finalData;
}

export const processFieldsForB4 = (schema, data, isBulk, dynamicHiddenMap = {}) => {
    let finalData = { ...data };
    schema.forEach((tab) => {
        tab.fields.forEach((field) => {
            finalData = processFields(field, finalData, false, dynamicHiddenMap);
        });
    });

    return finalData;
};

const getEditValuesAction = (entity, url, queryString) => {
    return {
        label: getLiteral('cfm_label_valuelist_update_values'),
        icon: 'edit',
        onClick: () => {
            entity &&
                logEvent({
                    event: entity.trueName,
                    submodule: 'valueList',
                    functionality: 'createField',
                });
            const state = Context.store.getState();
            const activeCrud = getActiveCrud(state);
            const data = activeCrud?.data;
            const originalData = activeCrud?.originalData;
            return isEqual(originalData, data, true)
                ? ensureRoute(url, queryString)
                : Context.store.dispatch(
                      Context?.actions?.EntityCrudActions?.toggleExtraFieldsModal(
                          true,
                          url,
                          queryString,
                      ),
                  );
        },
    };
};

/**
 * Whether the user may open standard value-list management from entity CRUD
 * (same rules as {@link getCustomizeCrudAction}).
 */
export const canCustomizeValueListFromEntityCrud = (permission, entity) =>
    !!(
        permission?.manageExtraFields &&
        permission?.canConfigImplementation &&
        entity?.withCustomizeLinkOnLists
    );

export const getCustomizeCrudAction = (entity, url, query) => {
    const permission = Context.store.getState().config?.permission;
    return canCustomizeValueListFromEntityCrud(permission, entity)
        ? [getEditValuesAction(entity, url, query)]
        : [];
};

export const getExtraCustomizeCrudAction = (entity, url, query) => {
    const permission = Context.store.getState().config?.permission;
    return canCustomizeValueListFromEntityCrud(permission, entity)
        ? [getEditValuesAction(entity, url, query)]
        : [];
};

export const toCrudSelectValue = (data, arr) => {
    const options = arr.reduce((obj, current) => {
        if (typeof current === 'string') {
            if (!data[current] || data[current] === '-1') return obj;
            let labelKey = current.replace('id', '');
            labelKey = labelKey.charAt(0).toLowerCase() + labelKey.slice(1);

            obj[current] = {
                label: data[labelKey],
                value: data[current],
            };
        } else if (
            typeof current === 'object' &&
            current.hasOwnProperty('label') &&
            current.hasOwnProperty('value')
        ) {
            if (!data[current.value] || data[current.value] === '-1') return obj;
            if (current.hasOwnProperty('id')) {
                obj[current.id] = {
                    label: data[current.label],
                    value: data[current.value],
                };
            } else {
                obj[current.value] = {
                    label: data[current.label],
                    value: data[current.value],
                };
            }
        }

        return obj;
    }, {});
    return options;
};

export const getActiveCrudName = (state) => {
    if (!state) state = Context.store.getState();
    const entityCrud = state?.entityCrud;
    const active =
        entityCrud?.activeModal?.length > 0
            ? entityCrud?.activeModal?.[entityCrud.activeModal.length - 1]
            : entityCrud?.active;
    return active || null;
};

export const getActiveCrud = (state) => {
    if (!state) state = Context.store.getState();
    const activeCrudName = getActiveCrudName(state);
    const entityCrud = state?.entityCrud;
    return entityCrud?.[activeCrudName] || {};
};

export const getActiveLateralCrudName = (state) => {
    if (!state) state = Context.store.getState();
    const active = state?.entityCrud?.active || '';
    return active;
};
